import type { MultiSubmitRow, SelectOption } from './types';
import { buildCostingRequest, hasCosting } from './costingPayload';
import type { CostingRequestDto } from './costingPayload';

/** Payload for POST /api/request-sample-multi-products (one entry per row that has rsChecked). */
export interface RequestSamplePayload {
  devOrderId: string;
  sampleRequest: {
    requestType: string;
    sampleNumber: string;
    sampleType: string;
    supplier: string;
    typeOfSampleType: string;
    colourwayGroup: string[];
    dueDate: string | null;
    factory: string | null;
    gradingGroupData: Array<{ size: string; len: string; uom: string; quantity: string; gradingGroup: string | null }>;
    trackingDates: TrackingDate[];
    parentId: string | null;
    revisionRootId: string | null;
    versionNumber: number;
  };
}

/** Build one request-sample row per MultiSubmitRow with rsChecked + sampleType set. */
export function buildRequestSamplePayloads(
  rows: MultiSubmitRow[],
  ctx: { supplierId: string; trackingDates: TrackingDate[] },
): RequestSamplePayload[] {
  return rows
    .filter((row) => row.rsChecked && row.sampleType)
    .map((row) => ({
      devOrderId: row.id,
      sampleRequest: {
        requestType: 'request',
        sampleNumber: row.sampleNumber || '',
        sampleType: row.sampleType,
        supplier: ctx.supplierId === INTERNAL_SUPPLIER_ID ? (row.rsSupplier || '') : (row.rsSupplier || ctx.supplierId),
        typeOfSampleType: row.typeOfSampleType || 'main',
        colourwayGroup: row.colourway ? [row.colourway] : [],
        dueDate: row.dueDate || null,
        factory: row.factory || null,
        gradingGroupData: row.gradingLines
          .filter((line) => line.gradingGroup || (line.size?.length ?? 0) > 0 || line.length || line.quantity || line.uom)
          .flatMap((line) =>
            (line.size && line.size.length > 0 ? line.size : ['']).map((size) => ({
              size,
              len: line.length,
              uom: line.uom,
              quantity: line.quantity,
              gradingGroup: line.gradingGroup || null,
            })),
          ),
        trackingDates: ctx.trackingDates,
        parentId: row.parentId,
        revisionRootId: row.revisionRootId,
        versionNumber: row.versionNumber,
      },
    }));
}

/** Tracking-date entry shape carried inside sampleRequest (built from the company sample-tracking master). */
export interface TrackingDate {
  label: string;
  value: string | null;
  trackingUrl: string | null;
  sampleId: string;
  key: string;
  _id: string;
}

export interface BuildContext {
  supplierId: string;
  trackingDates: TrackingDate[];
  timeZone: string;
  /** When present, each request carries a costingRequest built from that row's Costing Information. */
  supplierCurrencyById?: Record<string, string>;
}

export interface SubmitRequest {
  devOrderId: string;
  supplier: string;
  isTechPackCombined: boolean;
  requestType: 'Submit';
  to: string[];
  cc: string[];
  bcc: string[];
  isCommonMessage: boolean;
  message: string;
  selectedModules: string[];
  timeZone: string;
  bomDocuments: string[];
  colourDocuments: string[];
  otherDocuments: string[];
  designDocument: string[];
  selecteddesignDocumentOptions: string[];
  poDocuments: string[];
  sampleType: string[];
  pos: string[];
  documents: string[];
  tpArray: string[];
  status: 'Pending';
  specificationGradGroup: string[];
  pomImgsGradGroup: string[];
  cadGradGroup: string[];
  colours: string[];
  sampleRequest: {
    requestType: 'request';
    sampleNumber?: string;
    sampleType?: string;
    typeOfSampleType?: string;
    supplier: string;
    dueDate?: string | null;
    factory?: string | null;
    colourwayGroup: string[] | null;
    gradingGroupData: Array<{ size: string | null; len: string | null; uom: string | null; quantity: string | null; gradingGroup: string | null }>;
    trackingDates: TrackingDate[];
    sampleTypeGroupId?: string;
    parentId?: string | null;
    revisionRootId?: string | null;
    versionNumber?: number;
    isNewSample?: boolean;
    isAutoCreated?: boolean;
  };
  isSampleRequested: boolean;
  /** Costing Information + Core Product for this product; the backend persists it to ProductCostingRequests. */
  costingRequest?: CostingRequestDto;
  isInternal?: boolean;
}

export type ValidationResult = { ok: true } | { ok: false; errorKey: string; productNumber?: string };

const EMAIL_RE = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/;

/** Split a comma-separated email string into trimmed addresses and report validity. */
export function parseEmails(raw: string): { list: string[]; hasInvalid: boolean } {
  const parts = (raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  const hasInvalid = parts.some((s) => !EMAIL_RE.test(s.toLowerCase()));
  return { list: parts, hasInvalid };
}

const ids = (opts: SelectOption[]): string[] => opts.map((o) => o.value);

/** Technical-package modules eligible for the "combine into one document" path. */
const TP_ARRAY = ['design-details', 'bom', 'measurement', 'care', 'testing', 'sample'];

/** Module flags derived purely from the row's checkboxes (order mirrors the modal). */
function selectedModulesOf(row: MultiSubmitRow): string[] {
  const modules: string[] = [];
  if (row.cpChecked) modules.push('po');
  if (row.tpDesignDetails) modules.push('design-details');
  if (row.tpBom) modules.push('bom');
  if (row.tpSpecification) modules.push('measurement');
  if (row.tpTesting) modules.push('testing');
  if (row.tpCare) modules.push('care');
  if (row.tpSample) modules.push('sample');
  if (row.sdDesignDetails) modules.push('designDocument');
  if (row.sdColour) modules.push('colourDocument');
  if (row.sdBom) modules.push('bomDocument');
  if (row.sdSpecification) modules.push('otherSpecificationDoc');
  if (row.sdPomGuide) modules.push('pomImgs');
  if (row.sdPurchaseOrder) modules.push('poDocuments');
  if (row.sdTestingReport) modules.push('test-report');
  if (row.sdCareLayout) modules.push('care-label-layout');
  return modules;
}

/** Mirrors modal getTpAndSelectedVal: split combined TP modules into tpArray when combining. */
function splitTp(selectedModules: string[], combine: boolean): { tpArray: string[]; rest: string[] } {
  if (!combine) return { tpArray: [], rest: selectedModules };
  const tpArray = TP_ARRAY.filter((v) => selectedModules.includes(v));
  if (tpArray.length > 0) tpArray.unshift('dev-order');
  const rest = selectedModules.filter((v) => !TP_ARRAY.includes(v));
  return { tpArray, rest };
}

/** Build the sampleRequest sub-object. Carries real values only when row.rsChecked && row.sampleType
 *  (matches the legacy modal which only attached a meaningful sampleRequest when isSampleRequested). */
function buildSampleRequest(row: MultiSubmitRow, ctx: BuildContext): SubmitRequest['sampleRequest'] {
  const hasRs = !!(row.rsChecked && row.sampleType);
  if (!hasRs) {
    return {
      requestType: 'request',
      supplier: '',
      colourwayGroup: null,
      gradingGroupData: [{ size: null, len: null, uom: null, quantity: null, gradingGroup: null }],
      trackingDates: ctx.trackingDates,
    };
  }
  const gradingGroupData = row.gradingLines
    .filter((line) => line.gradingGroup || (line.size?.length ?? 0) > 0 || line.length || line.quantity || line.uom)
    .flatMap((line) =>
      (line.size && line.size.length > 0 ? line.size : [null]).map((size) => ({
        size: size ?? null,
        len: line.length || null,
        uom: line.uom || null,
        quantity: line.quantity || null,
        gradingGroup: line.gradingGroup || null,
      })),
    );
  return {
    requestType: 'request',
    sampleNumber: '',
    sampleType: row.sampleType,
    typeOfSampleType: row.typeOfSampleType || 'main',
    supplier: ctx.supplierId === INTERNAL_SUPPLIER_ID ? (row.rsSupplier || '') : (row.rsSupplier || ctx.supplierId),
    dueDate: row.dueDate || null,
    factory: row.factory || null,
    colourwayGroup: row.colourway ? [row.colourway] : [],
    gradingGroupData: gradingGroupData.length > 0
      ? gradingGroupData
      : [{ size: null, len: null, uom: null, quantity: null, gradingGroup: null }],
    trackingDates: ctx.trackingDates,
    ...(row.sampleTypeGroup ? { sampleTypeGroupId: row.sampleTypeGroup } : {}),
    parentId: row.parentId,
    revisionRootId: row.revisionRootId,
    versionNumber: row.versionNumber,
    isNewSample: true,
    isAutoCreated: true,
  };
}

/** Build one bulkSubmitDownload request per row, matching the single-product modal payload. */
export function buildSubmitRequests(rows: MultiSubmitRow[], ctx: BuildContext): SubmitRequest[] {
  const isInternal = ctx.supplierId === INTERNAL_SUPPLIER_ID;
  return rows.map((row) => {
    const modules = selectedModulesOf(row);
    const { tpArray, rest } = splitTp(modules, row.tpCombineYes);
    const to = parseEmails(row.emailTo).list;
    const cc = parseEmails(row.emailCc).list;
    const bcc = parseEmails(row.emailBcc).list;
    /* No Costing Information supplier means the user does not want a costing for this
       product, so the request ships without one. */
    const costingRequest = ctx.supplierCurrencyById && hasCosting(row)
      ? buildCostingRequest(row, { supplierCurrencyById: ctx.supplierCurrencyById })
      : undefined;
    return {
      devOrderId: row.id,
      supplier: isInternal ? '' : ctx.supplierId,
      isInternal,
      isTechPackCombined: row.tpCombineYes,
      requestType: 'Submit',
      to,
      cc,
      bcc,
      isCommonMessage: false,
      message: row.emailMessage || '',
      selectedModules: rest,
      timeZone: ctx.timeZone,
      bomDocuments: ids(row.sdBomValue),
      colourDocuments: ids(row.sdColourValue),
      otherDocuments: ids(row.sdSpecificationValue),
      designDocument: ids(row.sdDesignDetailsValue),
      selecteddesignDocumentOptions: ids(row.sdDesignDetailsValue),
      poDocuments: ids(row.sdPurchaseOrderValue),
      sampleType: ids(row.tpSampleValue),
      pos: ids(row.purchaseOrders),
      documents: [],
      tpArray,
      status: 'Pending',
      specificationGradGroup: ids(row.tpSpecificationValue),
      pomImgsGradGroup: ids(row.sdPomGuideValue),
      cadGradGroup: [],
      colours: row.cwChecked ? ids(row.productColourways) : [],
      sampleRequest: buildSampleRequest(row, ctx),
      isSampleRequested: !!(row.rsChecked && row.sampleType),
      ...(costingRequest ? { costingRequest } : {}),
    };
  });
}

/** Doc-modules whose checkbox requires at least one selected value (mirrors modal validationFun). */
const REQUIRED_VALUE: Array<{ when: (row: MultiSubmitRow) => boolean; has: (row: MultiSubmitRow) => boolean }> = [
  { when: (row) => row.sdSpecification, has: (row) => row.sdSpecificationValue.length > 0 },
  { when: (row) => row.sdColour, has: (row) => row.sdColourValue.length > 0 },
  { when: (row) => row.sdBom, has: (row) => row.sdBomValue.length > 0 },
  { when: (row) => row.sdPurchaseOrder, has: (row) => row.sdPurchaseOrderValue.length > 0 },
  { when: (row) => row.cpChecked, has: (row) => row.purchaseOrders.length > 0 },
  { when: (row) => row.tpSample, has: (row) => row.tpSampleValue.length > 0 },
];

const KEY = 'VALIDATION';

/** Validate every row against the modal's rules. First failure wins. errorKey is namespaced under modules/multi-submit. */
export function validateSubmit(rows: MultiSubmitRow[], supplierId: string): ValidationResult {
  if (!supplierId) return { ok: false, errorKey: `${KEY}.SELECT_SUPLIER` };
  for (const row of rows) {
    const modules = selectedModulesOf(row);
    const hasSampleRequest = !!(row.rsChecked && row.sampleType);
    if (modules.length === 0 && !hasSampleRequest) return { ok: false, errorKey: `${KEY}.SELECT_MODULE`, productNumber: row.productNumber };
    for (const rule of REQUIRED_VALUE) {
      if (rule.when(row) && !rule.has(row)) {
        return { ok: false, errorKey: `${KEY}.SELECT_AT_LEAST_ONE_DOCUMENT`, productNumber: row.productNumber };
      }
    }
    if (hasSampleRequest) {
      if (!row.rsSupplier) return { ok: false, errorKey: `${KEY}.SAMPLE_SUPPLIER_REQUIRED`, productNumber: row.productNumber };
      const anyLineMissingSize = row.gradingLines.some(
        (line) => (line.gradingGroup || line.length || line.quantity || line.uom) && (!line.size || line.size.length === 0),
      );
      if (anyLineMissingSize) return { ok: false, errorKey: `${KEY}.SELECT_SIZE_REQUIRED`, productNumber: row.productNumber };
    }
    const seenGradingGroups = new Set<string>();
    for (const line of row.gradingLines) {
      if (!line.gradingGroup) continue;
      if (seenGradingGroups.has(line.gradingGroup)) {
        return { ok: false, errorKey: `${KEY}.GRADING_GROUP_DUPLICATE`, productNumber: row.productNumber };
      }
      seenGradingGroups.add(line.gradingGroup);
    }
    const to = parseEmails(row.emailTo);
    if (to.list.length === 0 || to.hasInvalid) {
      return { ok: false, errorKey: `${KEY}.PLEASE_ADD_EMAIL`, productNumber: row.productNumber };
    }
    if (parseEmails(row.emailCc).hasInvalid || parseEmails(row.emailBcc).hasInvalid) {
      return { ok: false, errorKey: `${KEY}.PLEASE_ADD_VALID_EMAIL`, productNumber: row.productNumber };
    }
  }
  return { ok: true };
}

export const INTERNAL_SUPPLIER_ID = 'internal';
